<template>
    <div class="step apprentice-simple-card">
        <div class="row">
            <div class="header col-12">
                <div class="step-title">
                    <h3 class="title">
                        {{ stepIn.title !== null ? stepIn.title : `${$t('paper.step')} ${stepIn.order}` }}
                    </h3>
                    <div
                        class="deadline"
                        v-if="stepIn.deadlinedate">
                        <i class="btn btn-danger-icon-only btn-alarm-exclamation"></i
                        >{{ stepIn.deadline.date | date_written }}
                    </div>
                </div>

                <div class="step-status-overview">
                    <b-button
                        class="btn-icon-only status-info"
                        v-b-tooltip.hover
                        v-if="showWaiting"
                        variant="warning-icon-only"
                        :title="$t('paper.submitted_completed')"
                        data-toggle="tooltip"
                        data-placement="bottom"
                        :data-original-title="$t('paper.submitted_completed')"
                        ><i class="fas fa-hourglass-start"></i>
                    </b-button>

                    <b-button
                        class="btn-icon-only status-info"
                        v-b-tooltip.hover
                        v-if="showReopenStatus"
                        variant="danger-icon-only"
                        :title="$t('paper.again')"
                        data-toggle="tooltip"
                        data-placement="bottom"
                        :data-original-title="$t('paper.again')"
                        ><i class="fas fa-undo"></i>
                    </b-button>

                    <b-button
                        class="btn-icon-only status-info"
                        v-b-tooltip.hover
                        v-if="showClosedStatus"
                        variant="success-icon-only"
                        :title="$t('paper.approved')"
                        data-toggle="tooltip"
                        data-placement="bottom"
                        :data-original-title="$t('paper.approved')"
                        ><i class="fa fa-check"></i>
                    </b-button>

                    <b-button
                        class="btn-icon-only"
                        v-b-tooltip.hover
                        v-if="showSubmit"
                        variant="primary-icon"
                        :title="$t('paper.complete_submit')"
                        @click="onSubmit()"
                        data-toggle="tooltip"
                        data-placement="bottom"
                        :data-original-title="$t('paper.completed')"
                        ><i class="fas fa-check"></i> {{ $t('paper.completed') }}
                    </b-button>

                    <b-button
                        class="btn-icon-only"
                        v-b-tooltip.hover
                        v-if="showReopenStatus"
                        variant="primary-icon"
                        :title="$t('common.reset')"
                        @click="onReset()"
                        data-toggle="tooltip"
                        data-placement="bottom"
                        data-original-title="Reset"
                        ><i class="fas fa-redo"></i>
                    </b-button>

                    <b-button
                        class="btn-icon-only"
                        v-b-tooltip.hover
                        v-if="showClose"
                        variant="success-icon"
                        :title="$t('paper.approve')"
                        @click="setStatus1()"
                        data-toggle="tooltip"
                        data-placement="bottom"
                        :data-original-title="$t('paper.approve')"
                        ><i class="fa fa-check"></i>
                    </b-button>

                    <b-button
                        class="btn-icon-only"
                        v-b-tooltip.hover
                        v-if="showReopen"
                        variant="primary-icon"
                        :title="$t('paper.again')"
                        @click="setStatus2()"
                        data-toggle="tooltip"
                        data-placement="bottom"
                        :data-original-title="$t('paper.again')"
                        ><i class="fa fa-undo"></i>
                    </b-button>
                </div>
            </div>

            <design-2021-apprentice-display-html-component
                class="description col-12"
                :html-content="stepIn.description"></design-2021-apprentice-display-html-component>

            <div class="status-feedback col-12">
                <div class="row">
                    <div class="feedback order-1 order-lg-0 col-12 col-lg-8">
                        <h5 class="feedback-title">Feedback</h5>
                        <div
                            class="feedback-list"
                            v-if="stepIn.comments.length"
                            ref="messageListContainer">
                            <div
                                class="feedback-item"
                                v-for="commentblock in stepIn.comments"
                                :key="commentblock.id"
                                :class="commentblock.user_id === $authUser.id ? 'left' : 'right'">
                                <div class="user-info">
                                    <div class="credentials">
                                        {{
                                            commentblock.user ? commentblock.user.full_name : $t('common.deleted_user')
                                        }}
                                    </div>
                                    <div class="date">{{ commentblock.datetime }}</div>
                                </div>
                                <div class="feedback-content">
                                    <design-2021-apprentice-display-html-component
                                        class="feedback-comment"
                                        :html-content="
                                            commentblock.comment
                                        "></design-2021-apprentice-display-html-component>
                                    <div class="avatar">
                                        <b-avatar
                                            variant="primary"
                                            size="3rem"
                                            :src="getProfileImage(commentblock)">
                                        </b-avatar>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ckeditor
                            v-model="comment"
                            :editor="editor"
                            :config="editorConfig"
                            :placeholder="$t('paper.fill_in_feedback')">
                        </ckeditor>

                        <b-button
                            class="btn-save btn-feedback-save"
                            variant="primary"
                            @click="postComment"
                            :disabled="!comment">
                            {{ $t('common.save') }}
                        </b-button>
                    </div>

                    <div class="history order-0 order-lg-1 col-12 col-lg-4">
                        <h5 class="history-title">Updates</h5>
                        <ul class="history-list">
                            <li
                                class="history-item"
                                v-for="update in stepIn.updates"
                                :key="update.id">
                                <div class="status">
                                    <span
                                        class="notice"
                                        :class="getStatus(update.update)"
                                        >{{ update.update }}</span
                                    >
                                    <span class="user"
                                        >door
                                        {{ update.user ? update.user.full_name : $t('common.deleted_user') }}</span
                                    >
                                </div>
                                <div class="date">{{ update.datetime }}</div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ApprenticeEditor from '../../../helpers/ApprenticeEditor';

export default {
    name: 'design-2021-roadmap-step-tab-component',
    props: ['stepIn', 'projectIdIn'],
    data() {
        return {
            comment: '',
            editor: ApprenticeEditor,
            editorConfig: {
                contentsCss: 'test',
                bodyClass: 'mybodyclass',
                toolbar: {
                    items: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        'link',
                        'bulletedList',
                        'numberedList',
                        'blockQuote',
                        'undo',
                        'redo',
                    ],
                },
                language: this.$authUser.locale,
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
            },
        };
    },
    computed: {
        showSubmit() {
            const finished = this.stepIn.status.length === 1 ? this.stepIn.status[0].finished : 0;
            return !finished;
        },
        showWaiting() {
            const finished = this.stepIn.status.length === 1 ? this.stepIn.status[0].finished : 0;
            const status = this.stepIn.status.length === 1 ? this.stepIn.status[0].status : 0;
            return finished === 1 && status === 0;
        },
        showClose() {
            const finished = this.stepIn.status.length === 1 ? this.stepIn.status[0].finished : 0;
            const status = this.stepIn.status.length === 1 ? this.stepIn.status[0].status : 0;
            return finished === 1 && status === 0 && !this.$authUser.isStudent();
        },
        showReopen() {
            const finished = this.stepIn.status.length === 1 ? this.stepIn.status[0].finished : 0;
            return finished === 1 && !this.$authUser.isStudent();
        },
        showReopenStatus() {
            const finished = this.stepIn.status.length === 1 ? this.stepIn.status[0].finished : 0;
            const status = this.stepIn.status.length === 1 ? this.stepIn.status[0].status : 0;
            return finished === 0 && status === 2;
        },
        showClosedStatus() {
            const status = this.stepIn.status.length === 1 ? this.stepIn.status[0].status : 0;
            return status === 1;
        },
    },
    methods: {
        onSubmit: function () {
            const status = 0;
            const finished = 1;

            const data = {
                finished: finished,
                step_id: this.stepIn.id,
                project_id: this.projectIdIn,
            };

            axios
                .post('/roadmap/step/finished', data)
                .then((response) => {
                    if (response.data) {
                        this.$emit('updateTitleFinished', {
                            id: this.stepIn.id,
                            finished: finished,
                            status: status,
                            updates: response.data.updates,
                        });
                    }
                })
                .catch((error) => {});
        },

        setStatus1: function () {
            this.updateStatus(1);
        },

        setStatus2: function (status) {
            this.status = 2;
            this.finished = 0;
            this.updateStatus(2, 0);
        },

        onReset: function () {
            this.status = 0;
            this.finished = 0;
            this.updateStatus(0, 0);
        },

        updateStatus: function (status, finished) {
            finished =
                finished === undefined
                    ? this.stepIn.status.length === 1
                        ? this.stepIn.status[0].finished
                        : 0
                    : finished;

            axios
                .post('/roadmap/step/status', {
                    status: status,
                    step_id: this.stepIn.id,
                    project_id: this.projectIdIn,
                })
                .then((response) => {
                    if (response.data) {
                        this.$emit('updateTitleStatus', {
                            id: this.stepIn.id,
                            status: status,
                            updates: response.data.updates,
                            finished: finished,
                        });
                    }
                })
                .catch((error) => {});
        },

        postComment: function () {
            if (!this.comment) {
                return;
            }

            this.alert.saving();
            axios
                .post('/roadmap/step/comment', {
                    comment: this.comment,
                    step_id: this.stepIn.id,
                    project_id: this.projectIdIn,
                })
                .then((response) => {
                    if (response.data) {
                        this.alert.success();
                        this.$emit('updateComments', { id: this.stepIn.id, comments: response.data.comments });

                        this.comment = '';
                    }
                })
                .catch((error) => {
                    this.alert.ajaxError(error);
                });
        },

        // FIXME: Should not be matched against display strings
        getStatus(status) {
            switch (status) {
                case this.$t('paper.again'):
                    return 'color-danger';
                case this.$t('paper.approved'):
                    return 'color-success';
                case this.$t('paper.completed'):
                    return 'color-warning';
            }
        },

        getProfileImage(comment) {
            return comment.user?.profileimage?.file?.public_path ?? '/images/default_avatar_profile.jpg';
        },
    },
};
</script>

<style scoped lang="scss">
.step {
    .header,
    .description {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .header {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-content: center;

        .step-title,
        .step-status-overview {
            flex: 1 0 100%;
        }

        .step-title {
            margin-bottom: map-get($grid-gutter-spacing, 'eight');

            .title {
                margin-bottom: 0;
            }

            .deadline {
                display: flex;
                flex-direction: row;
                color: $text-muted-color;
                font-weight: $font-weight-medium;

                i {
                    font-style: normal;
                    cursor: default;
                }
            }
        }

        .step-status-overview {
            display: flex;
            justify-content: flex-end;
            align-self: flex-start;

            > .btn {
                &:not(:last-child) {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                }
            }

            .status-info {
                &,
                &:hover {
                    cursor: pointer;
                }
            }
        }
    }

    .description {
        color: $text-muted-color;
        font-size: 0.875rem;
        line-height: 1.5;

        p:last-child {
            margin-bottom: 0;
        }
    }

    .history {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .history-list {
        font-size: $default-font-size;
        list-style: none;
        margin-left: 0;
        padding-left: 0;
        margin-right: 0;

        .history-item {
            padding-top: map-get($grid-gutter-spacing, 'ten');

            .status {
                font-family: $headings-font-family;

                .notice {
                    font-weight: $font-weight-bold;
                }
            }

            .date {
                color: $text-muted-color;
                text-align: right;
            }

            &:not(:last-child) {
                border-bottom: 3px dotted $border-color;
                padding-bottom: map-get($grid-gutter-spacing, 'ten');
            }
        }
    }

    .feedback {
        .feedback-title {
            margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
        }

        .feedback-list {
            margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
        }

        .feedback-item {
            font-size: $default-font-size;

            &:not(:last-child) {
                margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
            }

            .user-info {
                display: flex;
                align-content: center;
                justify-content: space-between;
                margin-bottom: 5px;

                .credentials {
                    font-family: $headings-font-family;
                    margin-right: map-get($grid-gutter-spacing, 'fifteen');
                    font-weight: $font-weight-bold;
                }

                .date {
                    font-weight: $font-weight-bold;
                }
            }

            .feedback-content {
                display: flex;
                flex-direction: row;
                align-content: center;
                justify-content: flex-start;

                .feedback-comment {
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;
                    margin-right: map-get($grid-padding-spacing, 'twenty');
                    padding: map-get($grid-gutter-spacing, 'ten');
                    background-color: $info;
                    position: relative;
                    border-top-left-radius: map-get($grid-gutter-spacing, 'ten');
                    border-bottom-left-radius: map-get($grid-gutter-spacing, 'ten');
                    border-bottom-right-radius: map-get($grid-gutter-spacing, 'ten');

                    &:before {
                        content: '';
                        position: absolute;
                        display: block;
                        top: 0;
                    }

                    p:last-child {
                        margin-bottom: 0;
                    }

                    .btn {
                        font-size: 0.75rem;
                        flex-grow: 1;
                        justify-content: flex-end;
                    }
                }

                .avatar {
                    flex: 0 0 auto;
                }
            }

            &.left {
                .feedback-content {
                    flex-direction: row-reverse;

                    .feedback-comment {
                        margin-right: 0;
                        margin-left: map-get($grid-padding-spacing, 'twenty');
                        border-top-left-radius: 0;
                        border-top-right-radius: map-get($grid-gutter-spacing, 'ten');

                        &:before {
                            border-top: 1em solid $info;
                            border-left: 1em solid transparent;
                            left: -13px;
                        }
                    }
                }
            }

            &.right {
                .feedback-content .feedback-comment {
                    &:before {
                        border-top: 1em solid $info;
                        border-right: 1em solid transparent;
                        right: -13px;
                    }
                }
            }
        }

        .btn-feedback-save {
            float: right;
            margin-top: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .step {
        .status-feedback {
            .history {
                margin-bottom: 0;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .step {
        .header {
            .step-title,
            .step-status-overview {
                flex: 0 0 auto;
            }

            .step-title {
                width: 100%;
                max-width: calc(100% - 135px);
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
            }

            .step-status-overview {
                margin-left: auto;
            }
        }
    }
}
</style>
